import type { ColumnSort } from '@tanstack/react-table';
import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import {
  formatFiltersToUrlParams,
  parseFiltersFromUrlParams,
} from './parsers/filters';
import {
  formatPageNumberToUrlParams,
  parsePageNumberFromUrlParams,
} from './parsers/page';
import {
  formatSortByToUrlParams,
  parseSortByFromUrlParams,
} from './parsers/sortBy';
import type { FilterUrlParams, RosterUrlParamsHook } from './types';
import { areUrlParamsEqual } from './utils/areUrlParamsEqual';

export function useRosterUrlParams(): RosterUrlParamsHook {
  const [searchParams, setSearchParams] = useSearchParams();

  const filters = useMemo(() => {
    return parseFiltersFromUrlParams(searchParams);
  }, [searchParams]);

  const pageNumber = useMemo(() => {
    return parsePageNumberFromUrlParams(searchParams);
  }, [searchParams]);

  const sortBy = useMemo(() => {
    return parseSortByFromUrlParams(searchParams);
  }, [searchParams]);

  const setPageNumber = useCallback(
    (newPageNumber: number) => {
      setSearchParams(formatPageNumberToUrlParams(newPageNumber, searchParams));
    },
    [searchParams, setSearchParams],
  );

  const setSortBy = useCallback(
    (newSortBy: ColumnSort) => {
      setSearchParams(formatSortByToUrlParams(newSortBy, searchParams));
    },
    [setSearchParams, searchParams],
  );

  /**
   * Replaces the set of filters in the URL completely.
   * NOTE: A filter change always resets the other search params
   * (e.g. - page number) because we are doing a new search from scratch.
   * @param newFilters - The filters to set in the URL.
   */
  const setFilters = useCallback(
    (newFilters: Partial<FilterUrlParams>) => {
      const newUrlParams = formatFiltersToUrlParams(newFilters);
      const existingFilterUrlParams = formatFiltersToUrlParams(filters);

      if (areUrlParamsEqual(newUrlParams, existingFilterUrlParams)) {
        return;
      }

      setSearchParams(newUrlParams);
    },
    [filters, setSearchParams],
  );

  /**
   * Updates the filters in the URL with the new ones passed in.
   * NOTE: A filter change always resets the other search params
   * (e.g. - page number) because we are doing a new search from scratch.
   * @param filtersToUpdate - The filters to update/add to the URL.
   */
  const updateFilters = useCallback(
    (filtersToUpdate: Partial<FilterUrlParams>) => {
      const mergedFilters = { ...filters, ...filtersToUpdate };

      setFilters(mergedFilters);
    },
    [filters, setFilters],
  );

  return {
    filters,
    setFilters,
    updateFilters,
    pageNumber,
    setPageNumber,
    sortBy,
    setSortBy,
  };
}
